import { z } from "zod";

import { npiEnumerationTypeSchema } from "@/src/lib/npi-enumeration";
import type { AddressParts } from "@/src/models/address";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { type Practitioner } from "@/src/models/practitioner";

export enum OrderType {
  CONSULT = "consult",
  LAB = "lab",
  IMAGING = "imaging",
  PRESCRIPTION = "prescription",
  PROCEDURE = "procedure",
  OTHER = "other",
}

export enum OrderVerificationDecision {
  PROCESS = "process",
  ESCALATE = "escalate",
}

export enum OrderWorkflowStage {
  VERIFICATION = "verification",
  PROVIDER_SELECTION = "provider_selection",
  AUTHORIZATION = "authorization",
  SUBMISSION = "submission",
  COMPLETED = "completed",
  ESCALATED = "escalated",
}

export const TERMINAL_ORDER_WORKFLOW_STAGES = [OrderWorkflowStage.COMPLETED, OrderWorkflowStage.ESCALATED] as const;

export enum OrderProgressStatus {
  IN_PROCESS = "in_process",
  WAITING = "waiting",
  COMPLETED = "completed",
  ESCALATED = "escalated",
}

export enum OrderAuthorizationStatus {
  NOT_REQUIRED = "not_required",
  REQUIRED = "required",
  PENDING = "pending",
  APPROVED = "approved",
  NOT_APPROVED = "not_approved",
  /** Legacy value; treat as approved. */
  COMPLETED = "completed",
}

export function getOrderProgressStatus(order: {
  workflowStage: OrderWorkflowStage;
  authorizationStatus?: OrderAuthorizationStatus | null;
}): OrderProgressStatus {
  if (order.workflowStage === OrderWorkflowStage.COMPLETED) return OrderProgressStatus.COMPLETED;
  if (order.workflowStage === OrderWorkflowStage.ESCALATED) return OrderProgressStatus.ESCALATED;
  if (
    order.workflowStage === OrderWorkflowStage.AUTHORIZATION &&
    order.authorizationStatus === OrderAuthorizationStatus.PENDING
  ) {
    return OrderProgressStatus.WAITING;
  }

  return OrderProgressStatus.IN_PROCESS;
}

/** Athena order priority: `"1"` = STAT/high, `"2"` = routine/normal. */
export function isHighPriorityOrder(priority: string | null | undefined): boolean {
  return priority?.trim() === "1";
}

/** Outcome choices shown on the authorization details form. */
export const ORDER_AUTHORIZATION_STATUS_OPTIONS: {
  value: OrderAuthorizationStatus.APPROVED | OrderAuthorizationStatus.NOT_APPROVED | OrderAuthorizationStatus.PENDING;
  label: string;
}[] = [
  { value: OrderAuthorizationStatus.APPROVED, label: "Approved" },
  { value: OrderAuthorizationStatus.NOT_APPROVED, label: "Not approved" },
  { value: OrderAuthorizationStatus.PENDING, label: "Pending" },
];

export type OrderAuthorizationOutcomeStatus = (typeof ORDER_AUTHORIZATION_STATUS_OPTIONS)[number]["value"];

export function toOrderAuthorizationOutcomeStatus(
  status: OrderAuthorizationStatus | null | undefined
): OrderAuthorizationOutcomeStatus | "" {
  if (status === OrderAuthorizationStatus.APPROVED || status === OrderAuthorizationStatus.COMPLETED) {
    return OrderAuthorizationStatus.APPROVED;
  }

  if (status === OrderAuthorizationStatus.NOT_APPROVED || status === OrderAuthorizationStatus.PENDING) {
    return status;
  }

  return "";
}

export enum OrderAuthorizationSource {
  AVAILITY = "availity",
  UNITED_HEALTHCARE = "united_healthcare",
  EVICORE = "evicore",
  CARELON = "carelon",
  CIGNA = "cigna",
  OTHER = "other",
}

export const selectedOrderProviderAddressSchema = z.preprocess(
  (value) => {
    if (!value || typeof value !== "object" || Array.isArray(value)) {
      return value;
    }

    const address = value as Record<string, unknown>;

    return {
      line1: address.line1 ?? address.address1,
      line2: address.line2 ?? address.address2,
      city: address.city,
      state: address.state,
      postalCode: address.postalCode,
    };
  },
  z.object({
    line1: z.string().min(1),
    line2: z.string().optional(),
    city: z.string().min(1),
    state: z.string().min(1),
    postalCode: z.string().optional(),
  })
);

export const selectedOrderProviderSchema = z
  .object({
    npi: z.string().nullable().optional(),
    source: z.enum(["ppn", "athena"]),
    clinicalProviderId: z.string().optional(),
    displayName: z.string().min(1),
    npiEnumerationType: npiEnumerationTypeSchema,
    firstName: z.string().min(1).optional(),
    lastName: z.string().min(1).optional(),
    address: selectedOrderProviderAddressSchema.optional(),
    phone: z.string().optional(),
    fax: z.string().optional(),
  })
  .superRefine((provider, ctx) => {
    if (provider.npiEnumerationType === "NPI-1" && (!provider.firstName || !provider.lastName)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "NPI-1 providers require firstName and lastName",
        path: ["firstName"],
      });
    }
  });

export type SelectedOrderProviderAddress = z.infer<typeof selectedOrderProviderAddressSchema>;
export type SelectedOrderProvider = z.infer<typeof selectedOrderProviderSchema>;

export function toSelectedOrderProviderAddress(
  parts: AddressParts | null | undefined
): SelectedOrderProviderAddress | undefined {
  if (!parts?.line1 || !parts.city || !parts.state) {
    return undefined;
  }

  return {
    line1: parts.line1,
    ...(parts.line2 ? { line2: parts.line2 } : {}),
    city: parts.city,
    state: parts.state,
    ...(parts.postalCode ? { postalCode: parts.postalCode } : {}),
  };
}

export const patientOrderSchema = z.object({
  patientId: z.string().uuid(),
  ehrId: z.string(),
  orderType: z.nativeEnum(OrderType),
  status: z.string(),
  orderingProviderId: z.string().uuid().nullable(),
  dateOrdered: z.string().nullable(),
  description: z.string().nullable(),
  ehrOrderClassification: z.string().nullable(),
  orderClassification: z.string().nullable(),
  rawResponse: z.unknown().optional(),
  displayId: z.string().nullable().optional(),
  clinicalProviderEhrId: z.string().nullable(),
  clinicalProviderName: z.string().nullable(),
  clinicalProviderNpi: z.string().nullable(),
  procedureCode: z.string().nullable(),
  departmentId: z.string().nullable(),
  priority: z.string().nullable(),
  diagnoses: z.array(z.unknown()),
  verificationDecision: z.nativeEnum(OrderVerificationDecision).nullable(),
  verificationReason: z.string().nullable(),
  workflowStage: z.nativeEnum(OrderWorkflowStage),
  selectedProvider: selectedOrderProviderSchema.nullable(),
  authorizationStatus: z.nativeEnum(OrderAuthorizationStatus).nullable(),
  authorizationSource: z.string().nullable(),
  authorizationId: z.string().nullable(),
  authorizationNotes: z.string().nullable(),
  authorizationCompletedAt: z.string().nullable(),
  submissionCompletedAt: z.string().nullable(),
  submissionPatientCaseAt: z.string().nullable(),
  submissionSmsSentAt: z.string().nullable(),
});

export type PatientOrder = z.infer<typeof patientOrderSchema> & DatabaseDefaults;

export type OrderClinicalAssignment = {
  clinicalProviderId: string;
  name: string | null;
  npi: string | null;
  address: AddressParts | null;
  phone: string | null;
  fax: string | null;
};

export type LivePatientContact = {
  preferredName: string | null;
  consentToText: boolean | null;
};

export type PatientOrderWithPatient = PatientOrder & {
  patient?: { givenName: string; familyName: string; displayId: string };
  orderingProviderPractitioner?: Practitioner | null;
  orderedClinicalAssignment?: OrderClinicalAssignment | null;
  liveContact?: LivePatientContact | null;
};

export const patientOrderShape: PatientOrder = {
  id: "",
  groupId: "",
  patientId: "",
  ehrId: "",
  orderType: OrderType.OTHER,
  status: "",
  orderingProviderId: null,
  dateOrdered: null,
  description: null,
  ehrOrderClassification: null,
  orderClassification: null,
  rawResponse: {},
  displayId: null,
  clinicalProviderEhrId: null,
  clinicalProviderName: null,
  clinicalProviderNpi: null,
  procedureCode: null,
  departmentId: null,
  priority: null,
  diagnoses: [],
  verificationDecision: null,
  verificationReason: null,
  workflowStage: OrderWorkflowStage.VERIFICATION,
  selectedProvider: null,
  authorizationStatus: null,
  authorizationSource: null,
  authorizationId: null,
  authorizationNotes: null,
  authorizationCompletedAt: null,
  submissionCompletedAt: null,
  submissionPatientCaseAt: null,
  submissionSmsSentAt: null,
  createdAt: "",
  updatedAt: "",
  deletedAt: null,
};

export type PatientOrderSyncPayload = Omit<
  PatientOrder,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "verificationDecision"
  | "verificationReason"
  | "workflowStage"
  | "selectedProvider"
  | "authorizationStatus"
  | "authorizationSource"
  | "authorizationId"
  | "authorizationNotes"
  | "authorizationCompletedAt"
  | "submissionCompletedAt"
  | "submissionPatientCaseAt"
  | "submissionSmsSentAt"
>;
